import React, { useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Scale, PenLine, FileSearch, UserCheck, Link2, Upload, ShieldAlert } from "lucide-react";

const ACTIONS = [
  { value: "lawyer_review_room", label: "Create Lawyer Review Room", icon: Scale, needs: ["professional", "scheduled_at"] },
  { value: "professional_signing_room", label: "Create Professional Signing Room", icon: PenLine, needs: ["professional", "scheduled_at"] },
  { value: "document_review_room", label: "Create Document Review Room", icon: FileSearch, needs: ["professional", "scheduled_at"] },
  { value: "assign_professional", label: "Assign lawyer / professional", icon: UserCheck, needs: ["professional_required"] },
  { value: "add_signing_link", label: "Add external signing link", icon: Link2, needs: ["link"] },
  { value: "request_signed_upload", label: "Request signed document upload", icon: Upload, needs: ["professional"] },
];

export default function HandoffDialog({ open, onOpenChange, docId, meta, onDone }) {
  const [action, setAction] = useState("lawyer_review_room");
  const [f, setF] = useState({ professional_user_id: "", scheduled_at: "", external_signing_link: "", external_signing_provider: "docusign" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const cfg = ACTIONS.find((a) => a.value === action);
  const needs = (n) => cfg?.needs.includes(n);

  const submit = async () => {
    if (needs("professional_required") && !f.professional_user_id) return toast.error("Select a lawyer/professional.");
    if (needs("link") && !f.external_signing_link.trim()) return toast.error("Enter the external signing link.");
    setSaving(true);
    try {
      const payload = { action };
      if (f.professional_user_id) payload.professional_user_id = f.professional_user_id;
      if (needs("scheduled_at") && f.scheduled_at) payload.scheduled_at = new Date(f.scheduled_at).toISOString();
      if (needs("link")) { payload.external_signing_link = f.external_signing_link.trim(); payload.external_signing_provider = f.external_signing_provider; }
      await api.post(`/doc-center/documents/${docId}/signing/handoff`, payload);
      toast.success("Signing handoff created.");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const pros = meta?.professionals || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="signing-handoff-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1C4358]">Create signing handoff?</DialogTitle>
          <DialogDescription className="text-[12px]">Hand this document off for review or signing. Nothing is created until you confirm.</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg bg-[#FFF7ED] border border-[#FED7AA] px-3 py-2 flex items-start gap-2 mb-1" data-testid="handoff-disclaimer">
          <ShieldAlert size={15} className="text-[#B7791F] shrink-0 mt-0.5" />
          <span className="text-[11.5px] text-[#92600E]">{meta?.disclaimer || "MovingMyMama does not provide legal advice. Legal questions should be reviewed with a qualified lawyer or professional."}</span>
        </div>

        <div className="space-y-2">
          {ACTIONS.map((a) => (
            <label key={a.value} data-testid={`handoff-action-${a.value}`}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${action === a.value ? "border-[#1C4358] bg-[#1C4358]/[0.05]" : "border-[#E6EDF2] hover:bg-[#F7FAFC]"}`}>
              <input type="radio" name="handoff" checked={action === a.value} onChange={() => setAction(a.value)} className="accent-[#1C4358]" />
              {React.createElement(a.icon, { size: 15, className: "text-[#1C4358]" })}
              <span className="text-[13px] font-medium text-[#252221]">{a.label}</span>
            </label>
          ))}
        </div>

        <div className="space-y-3 mt-2">
          {(needs("professional") || needs("professional_required")) && (
            <div>
              <Label className="text-[12px]">Lawyer / professional {needs("professional_required") && "*"}</Label>
              <Select value={f.professional_user_id || "none"} onValueChange={(v) => set("professional_user_id", v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 text-[13px]" data-testid="handoff-professional"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent><SelectItem value="none">{needs("professional_required") ? "Select…" : "None / decide later"}</SelectItem>
                  {pros.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              {!pros.length && <p className="text-[11px] text-[#9C1E22] mt-0.5">No professional external accounts in your scope yet.</p>}
            </div>
          )}
          {needs("scheduled_at") && (
            <div><Label className="text-[12px]">Meeting date/time (optional)</Label>
              <Input type="datetime-local" value={f.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} className="h-9 text-[13px]" data-testid="handoff-scheduled" /></div>
          )}
          {needs("link") && (
            <>
              <div><Label className="text-[12px]">External signing link *</Label>
                <Input value={f.external_signing_link} onChange={(e) => set("external_signing_link", e.target.value)} placeholder="https://…" className="h-9 text-[13px]" data-testid="handoff-link" /></div>
              <div><Label className="text-[12px]">Provider</Label>
                <Select value={f.external_signing_provider} onValueChange={(v) => set("external_signing_provider", v)}>
                  <SelectTrigger className="h-9 text-[13px]" data-testid="handoff-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.providers || []).filter((p) => p.value !== "not_selected").map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9" data-testid="handoff-skip">Skip for now</Button>
          <Button onClick={submit} disabled={saving} className="h-9 bg-[#1C4358] hover:bg-[#153341] text-white" data-testid="handoff-confirm">
            {saving ? <Loader2 size={15} className="animate-spin mr-1" /> : null} Confirm handoff
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
